'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface OfferItem {
  id: string;
  dishName: string;
  theme: string;
  dateMood: string;
  description: string;
  imageUrl: string;
}

export default function NotificationsInteractive() {
  const [offers, setOffers] = useState<OfferItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function fetchOffers() {
      try {
        const res = await fetch('/api/offers', { cache: 'no-store' });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (Array.isArray(data)) {
          setOffers(
            data.map((o: { id: string; dishName: string; theme: string; dateMood: string; description?: string; imageUrl: string }) => ({
              id: o.id,
              dishName: o.dishName,
              theme: o.theme,
              dateMood: o.dateMood,
              description: o.description ?? '',
              imageUrl: o.imageUrl,
            }))
          );
        }
      } catch (_) {
        if (!cancelled) setOffers([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchOffers();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-foreground flex items-center gap-2">
            <Icon name="BellIcon" size={32} className="text-primary" />
            Offers & Notifications
          </h1>
          <p className="mt-2 text-muted-foreground">
            Current offers and membership benefits. Everything you upload from Admin Offer Upload appears here.
          </p>
        </div>

        {/* Offers details – from Admin Offer Upload */}
        <section className="mb-12">
          <h2 className="font-heading text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="TagIcon" size={22} />
            Current offers
          </h2>
          {loading ? (
            <div className="grid gap-4 sm:grid-cols-2">
              {[1, 2].map((i) => (
                <div key={i} className="rounded-xl border border-border bg-card overflow-hidden animate-pulse">
                  <div className="aspect-[4/3] bg-muted" />
                  <div className="p-4 space-y-2">
                    <div className="h-4 bg-muted rounded w-3/4" />
                    <div className="h-3 bg-muted rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : offers.length === 0 ? (
            <div className="rounded-xl border border-border bg-card p-8 text-center text-muted-foreground">
              <Icon name="TagIcon" size={48} className="mx-auto mb-3 opacity-50" />
              <p>No offers right now. Check back later or visit the menu for current specials.</p>
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2">
              {offers.map((offer) => (
                <div
                  key={offer.id}
                  className="rounded-xl border border-border bg-card overflow-hidden shadow-sm hover:shadow-md transition-smooth"
                >
                  <div className="relative aspect-[4/3] bg-muted">
                    <AppImage
                      src={offer.imageUrl}
                      alt={offer.dishName}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                      <span className="inline-block rounded-full bg-primary/90 px-3 py-1 text-xs font-medium text-primary-foreground">
                        {offer.theme}
                      </span>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-foreground">{offer.dishName}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{offer.dateMood}</p>
                    {offer.description && (
                      <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{offer.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Membership details */}
        <section className="rounded-xl border border-border bg-card p-6 shadow-sm">
          <h2 className="font-heading text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="StarIcon" size={22} className="text-amber-500" />
            Membership details
          </h2>
          <div className="space-y-4 text-muted-foreground">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                <Icon name="TruckIcon" size={20} className="text-primary" />
              </div>
              <div>
                <p className="font-medium text-foreground">Free delivery</p>
                <p className="text-sm mt-0.5">
                  Free delivery on orders above ₹199. Conditions apply; may vary by location.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-500/10">
                <Icon name="GiftIcon" size={20} className="text-amber-600" />
              </div>
              <div>
                <p className="font-medium text-foreground">Exclusive offers</p>
                <p className="text-sm mt-0.5">
                  Members get first access to new offers and seasonal promotions.
                </p>
              </div>
            </div>
            <p className="text-sm pt-2 border-t border-border">
              For membership sign-up or queries, visit our{' '}
              <Link href="/contact-us" className="text-primary underline hover:no-underline">
                Contact
              </Link>{' '}
              page or call during business hours.
            </p>
          </div>
        </section>

        <div className="mt-8 text-center">
          <Link
            href="/menu-catalog"
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus-ring"
          >
            <Icon name="Squares2X2Icon" size={20} />
            View menu
          </Link>
        </div>
      </div>
    </div>
  );
}
